import React, { useState, useEffect } from 'react';
import { apiRequest } from '../utils/apiHelper';

import FeatureProduct from '../landing/FeatureProduct';
import ScrollToTop from '../components/ScrollToTop';

import LoadingSpinner from '../components/LoadingSpinner';
import { showSuccess, showError, showInfo } from '../utils/toastHelper';

const petTypes = [
  { id: 'all', label: 'Tümü', icon: '🐾' },
  { id: 'kedi', label: 'Kedi', icon: '🐱' },
  { id: 'kopek', label: 'Köpek', icon: '🐕' }
];

const ageGroups = [
  { id: 'all', label: 'Tümü' },
  { id: 'yavru', label: 'Yavru' },
  { id: 'yetiskin', label: 'Yetişkin' }
];

const sizes = [
  { id: 'all', label: 'Tümü' },
  { id: 'mini', label: 'Mini' },
  { id: 'orta', label: 'Orta' },
  { id: 'buyuk', label: 'Büyük' }
];

const productTypes = [
  { id: 'kito-fresh', label: 'Kito Fresh' },
  { id: 'kuru-mama', label: 'Kuru Mama' },
  { id: 'odul-mamasi', label: 'Ödül Maması' },
  { id: 'vitamin', label: 'Vitamin' }
];

function ProductList() {
  const [products, setProducts] = useState([]);
  const [displayedProducts, setDisplayedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    petType: 'Tümü',
    ageGroup: 'Tümü',
    size: 'Tümü',
    productTypes: []
  });
  const [loadingMore, setLoadingMore] = useState(false);
  const [displayCount, setDisplayCount] = useState(12); // Başlangıçta gösterilecek ürün sayısı
  const [showFilters, setShowFilters] = useState(false); // Filtrelerin görünürlüğünü kontrol etmek için
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [activeFilterCount, setActiveFilterCount] = useState(0);

  // Toggle filters visibility on mobile
  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  // Responsive davranış için pencere boyutu değişikliklerini izle
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Aktif filtre sayısını hesapla
  useEffect(() => {
    let count = 0;
    if (filters.petType !== 'Tümü') count++;
    if (filters.ageGroup !== 'Tümü') count++;
    if (filters.size !== 'Tümü') count++;
    count += filters.productTypes.length;
    setActiveFilterCount(count);
  }, [filters]);

  // Sayfa yüklendiğinde scroll pozisyonunu sıfırla
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const loadAllProducts = async () => {
    try {
      setLoading(true);

      // Query parametrelerini oluştur
      const params = new URLSearchParams();

      if (searchTerm) {
        params.append('search', searchTerm);
      }

      // Filtreleri hazırla
      const activeFilters = [];

      // Single select filtrelerini ekle (Tümü değilse)
      if (filters.petType !== 'Tümü') {
        activeFilters.push(filters.petType);
      }
      if (filters.ageGroup !== 'Tümü') {
        activeFilters.push(filters.ageGroup);
      }
      if (filters.size !== 'Tümü') {
        activeFilters.push(filters.size);
      }

      // Multiple select product types'ı ekle
      activeFilters.push(...filters.productTypes);

      // Filtreleri sadece dolu olduğunda ekle
      if (activeFilters.length > 0) {
        params.append('filters', JSON.stringify(activeFilters));
      }

      // Tüm ürünleri tek seferde al
      params.append('limit', '100'); // Maksimum ürün sayısı
      // Stok bilgisini de getir
      params.append('includeStock', 'true');

      const response = await apiRequest(`/products?${params.toString()}`);

      if (!response.success) {
        throw new Error(response.message || 'Ürünler yüklenirken bir hata oluştu');
      }

      const allProducts = response.products || [];
      setProducts(allProducts);

      // İlk 12 ürünü göster
      setDisplayedProducts(allProducts.slice(0, displayCount));
      setError(null);
    } catch (error) {
      // 401 hatası için ek toast gösterme (zaten showSessionTimeout çağrıldı)
      if (error.isAuthError) {
        setError('Oturum süresi doldu. Lütfen tekrar giriş yapın.');
        return;
      }
      // apiError durumunda showServerError() artık apiHelper içinde otomatik çağrılıyor
      if (!error.apiError) {
        setError('Ürünler yüklenirken bir hata oluştu');
        showError('Ürünler yüklenirken bir hata oluştu');
      } else {
        setError('Sunucuya ulaşılamıyor. Lütfen internet bağlantınızı kontrol edin.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Daha fazla ürün göster
  const loadMore = () => {
    setLoadingMore(true);
    const nextProducts = products.slice(displayedProducts.length, displayedProducts.length + 12);
    setDisplayedProducts(prev => [...prev, ...nextProducts]);
    setLoadingMore(false);
  };

  // Tüm filtreleri temizle
  const clearAllFilters = () => {
    setFilters({
      petType: 'Tümü',
      ageGroup: 'Tümü',
      size: 'Tümü',
      productTypes: []
    });
    setSearchTerm('');
    setDisplayCount(12);
  };

  // Filtre değişikliklerini handle et
  const handleFilterChange = (filterType, value) => {
    const currentScrollPosition = window.scrollY;
    if (filterType === 'productTypes') {
      // Product type için multiple select
      setFilters(prev => ({
        ...prev,
        productTypes: prev.productTypes.includes(value)
          ? prev.productTypes.filter(type => type !== value)
          : [...prev.productTypes, value]
      }));
    } else {
      // Diğer filtreler için single select
      setFilters(prev => ({
        ...prev,
        [filterType]: value
      }));
    }
    setDisplayCount(12); // Filtreleme yapıldığında gösterilen ürün sayısını sıfırla
    window.scrollTo(0, currentScrollPosition);
  };

  // Search değişikliklerini handle et
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setDisplayCount(12); // Arama yapıldığında gösterilen ürün sayısını sıfırla
  };

  // Initial load ve search/filter değişikliklerinde
  useEffect(() => {
    loadAllProducts();
  }, [searchTerm, filters]);

  // Daha fazla göster butonunun görünürlüğü
  const hasMore = displayedProducts.length < products.length;

  if (error) {
    return (
      <div className="container py-4">
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="d-flex flex-column min-vh-100">
      {/* Hero Section */}
      <div className="bg-orange text-white py-3">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-7 mb-3 mb-lg-0">
              <h1 className={`${isMobile ? 'fs-3' : 'fs-2'} fw-bold mb-2`}>Kito Mama Aboneliği</h1>
              <p className={`${isMobile ? 'small mb-2' : 'mb-3'} opacity-90`} style={{ fontSize: isMobile ? '13px' : '17px' }}>
                Dostunuz için en taze ve sağlıklı mamaları seçin, düzenli olarak kapınıza gelsin.
              </p>
              <div className="position-relative">
                <input
                  type="text"
                  className="form-control pe-4"
                  placeholder="Mama aramaya başlayın..."
                  value={searchTerm}
                  onChange={handleSearch}
                  style={{
                    fontSize: '17px',
                    padding: '12px 24px',
                    borderRadius: '50px',
                    border: 'none',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
                  }}
                />
                <i className="fas fa-search position-absolute top-50 end-0 translate-middle-y me-3 text-muted" style={{ fontSize: '14px' }}></i>
              </div>
            </div>
            <div className="col-lg-5 text-center d-none d-lg-block">
              <img
                src="https://cdn.shopify.com/s/files/1/0689/4059/5500/files/dana_3101_7b64fd1a-e678-4b83-bebf-401379061662.png?v=1709643278"
                alt="Kito Mama"
                className="img-fluid"
                style={{ maxHeight: '200px' }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Filter Section */}
      <div className="bg-light py-4 pb-2 position-relative">
        <div className="container">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <h5 className="mb-0 fw-bold text-dark">Filtreler</h5>
            <div className="d-md-none">
              <button
                className="btn btn-outline-olive d-flex align-items-center justify-content-between"
                onClick={toggleFilters}
              >
                <span>
                  <i className="fas fa-filter me-2"></i>
                  {showFilters ? 'Gizle' : 'Filtrele'}
                </span>
                {activeFilterCount > 0 && (
                  <span className="badge bg-olive text-white ms-2">{activeFilterCount}</span>
                )}
                <i className={`fas fa-chevron-${showFilters ? 'up' : 'down'} ms-2`}></i>
              </button>
            </div>
          </div>
          <div className={`filter-container ${showFilters ? 'show-filters' : ''}`}>
            {/* Pet Type Filter */}
            <div className="filter-section">
              <div className="filter-title">Evcil Hayvan</div>
              <div className="d-flex gap-2 justify-content-center">
                {petTypes.map(type => (
                  <button
                    key={type.id}
                    className={`filter-btn ${filters.petType === type.label ? 'active' : ''}`}
                    onClick={() => handleFilterChange('petType', type.label)}
                  >
                    <span className="me-1">{type.icon}</span>
                    {type.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Age Group Filter */}
            <div className="filter-section">
              <div className="filter-title">Yaş Grubu</div>
              <div className="d-flex gap-2 justify-content-center">
                {ageGroups.map(age => (
                  <button
                    key={age.id}
                    className={`filter-btn ${filters.ageGroup === age.label ? 'active' : ''}`}
                    onClick={() => handleFilterChange('ageGroup', age.label)}
                  >
                    {age.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Size Filter */}
            <div className="filter-section">
              <div className="filter-title">Irk</div>
              <div className="d-flex gap-2 justify-content-center">
                {sizes.map(size => (
                  <button
                    key={size.id}
                    className={`filter-btn ${filters.size === size.label ? 'active' : ''}`}
                    onClick={() => handleFilterChange('size', size.label)}
                  >
                    {size.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Product Type Filter */}
            <div className="filter-section">
              <div className="filter-title">Ürün Tipi</div>
              <div className="d-flex gap-2 justify-content-center flex-wrap">
                {productTypes.map(type => (
                  <button
                    key={type.id}
                    className={`filter-btn ${filters.productTypes.includes(type.label) ? 'active' : ''}`}
                    onClick={() => handleFilterChange('productTypes', type.label)}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Alt Kısım - Ürün Sayısı ve Filtreleri Temizle */}
          <div className="d-flex align-items-center justify-content-between mt-3">
            <div>
              {displayedProducts.length > 0 && (
                <div className={`${isMobile ? 'small' : ''} text-muted`}>
                  <strong>{products.length}</strong> ürün bulundu
                </div>
              )}
            </div>

            {/* Filtreleri Sıfırla Butonu - Sağ Altta */}
            {activeFilterCount > 0 && (
              <button
                className="btn d-flex align-items-center"
                onClick={clearAllFilters}
                style={{
                  fontSize: '13px',
                  fontWeight: '600',
                  padding: '8px 16px',
                  borderRadius: '20px',
                  transition: 'all 0.2s',
                  backgroundColor: '#6c757d',
                  border: '1px solid #6c757d',
                  color: '#ffffff'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#5a6268';
                  e.currentTarget.style.borderColor = '#5a6268';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#6c757d';
                  e.currentTarget.style.borderColor = '#6c757d';
                }}
              >
                <i className="fas fa-redo me-2" style={{ fontSize: '11px' }}></i>
                {isMobile ? 'Sıfırla' : 'Filtreleri Sıfırla'}
                {activeFilterCount > 0 && (
                  <span className="badge bg-white text-dark ms-2" style={{
                    fontSize: '10px',
                    padding: '2px 6px',
                    borderRadius: '10px',
                    fontWeight: '600'
                  }}>
                    {activeFilterCount}
                  </span>
                )}
              </button>
            )}
          </div>
        </div>

        <style>
          {`
            .filter-container {
              display: flex;
              justify-content: space-between;
              gap: 9px;
            }

            @media (max-width: 1200px) {
              .filter-container {
                flex-wrap: wrap;
              }
              .filter-section {
                width: 48% !important;
                margin-bottom: 12px;
              }
            }

            @media (max-width: 768px) {
              .filter-container {
                flex-direction: column;
                display: none;
                transition: all 0.3s ease;
                margin-top: 0;
                overflow: hidden;
                animation: slideDown 0.3s ease;
              }
              .filter-container.show-filters {
                display: flex;
                margin-top: 1rem;
              }
              .filter-section {
                width: 100% !important;
                margin-bottom: 10px;
              }
              .filter-btn {
                flex: 1;
                min-width: auto !important;
                padding: 6px 8px !important;
              }
              .filter-section .d-flex {
                flex-wrap: wrap;
              }
              @keyframes slideDown {
                from {
                  opacity: 0;
                  transform: translateY(-10px);
                }
                to {
                  opacity: 1;
                  transform: translateY(0);
                }
              }
            }

            .btn-outline-olive {
              color: #4A5D3A;
              border-color: #4A5D3A;
              background: white;
              padding: 10px 16px;
              font-weight: 500;
              transition: all 0.2s;
            }
            
            .bg-olive {
              background-color: #4A5D3A;
            }

            .btn-outline-olive:hover {
              background: #4A5D3A;
              color: white;
            }

            .filter-section {
              background: white;
              border-radius: 8px;
              overflow: hidden;
              box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
              width: 22%;
            }
            
            .filter-section:last-child {
              width: 34%;
            }
            
            .filter-title {
              background: #4A5D3A;
              color: white;
              padding: 6px 10px;
              text-align: center;
              font-weight: 500;
              font-size: 15px;
              margin-bottom: 6px;
            }
            
            .filter-btn {
              border: 1px solid #E5E7EB;
              background: white;
              color: #4A5D3A;
              padding: 6px 14px;
              border-radius: 16px;
              font-size: 15px;
              transition: all 0.2s;
              margin: 0 1px 6px 1px;
              min-width: 50px;
              white-space: nowrap;
            }

            .filter-section .d-flex {
              gap: 2px !important;
              padding: 0 2px;
            }

            .filter-btn:hover {
              border-color: #4A5D3A;
              color: #4A5D3A;
              transform: translateY(-1px);
              box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
            }

            .filter-btn.active {
              background: #4A5D3A;
              color: white;
              border: 2px solid #ffffff;
              padding: 5px 13px;
              font-weight: 600;
              box-shadow: 0 0 0 2px #dfe4d9ff, 0 4px 10px rgba(221, 227, 216, 0.2);
            }

            .filter-btn.active:hover {
              background: #3e4f30;
              border-color: #ffffff;
              box-shadow: 0 0 0 2px #708162, 0 6px 14px rgba(74, 93, 58, 0.25);
            }
          `}
        </style>
      </div>

      {/* Products Grid */}
      <div className="container py-2">
        {loading ? (
          <div className="d-flex justify-content-center align-items-center py-5">
            <LoadingSpinner message="Ürünler yükleniyor..." size="medium" />
          </div>
        ) : displayedProducts.length === 0 ? (
          <div className="alert alert-info text-center">
            <i className="fas fa-search-minus fa-2x mb-3"></i>
            <p>Aradığınız kriterlere uygun ürün bulunamadı.</p>
            <button className="btn btn-sm btn-outline-primary" onClick={clearAllFilters}>
              Tüm filtreleri temizle
            </button>
          </div>
        ) : (
          <>
            <div className="row row-cols-2 row-cols-sm-2 row-cols-md-3 row-cols-lg-4 g-3 g-md-4">
              {displayedProducts.map(product => (
                <FeatureProduct key={product.id} product={product} />
              ))}
            </div>

            {hasMore && (
              <div className="text-center mt-4">
                <button
                  className="btn btn-outline-olive px-4"
                  onClick={loadMore}
                  disabled={loadingMore}
                >
                  {loadingMore ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Yükleniyor...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-plus-circle me-2"></i>
                      Daha Fazla Göster ({products.length - displayedProducts.length})
                    </>
                  )}
                </button>
              </div>
            )}

            <style jsx="true">{`
              .row {
                margin-left: -8px;
                margin-right: -8px;
              }
              
              .col {
                padding: 8px;
              }
              
              @media (max-width: 576px) {
                .row {
                  margin-left: -6px;
                  margin-right: -6px;
                }
                
                .col {
                  padding: 6px;
                }
              }
              
              @media (min-width: 992px) {
                .g-md-4 {
                  --bs-gutter-y: 1.8rem !important;
                }
              }
            `}</style>
          </>
        )}
      </div>

      {/* Scroll to Top Button */}
      <ScrollToTop />
    </div>
  );
}

export default ProductList;
